import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Form, FormButton } from '../../../Common';
import { categoryService } from '../../../../services';
import { adminImageService } from '../../../../services/adminImageService';
import { toast } from 'react-toastify';
import type { OccasionLocale } from '../../../../types/category.types';
import ExperienceFormContent from '../ExperienceFormContent';
import './AddExperienceForm.css';

interface AddExperienceFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const AddExperienceForm: React.FC<AddExperienceFormProps> = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [locale, setLocale] = useState<OccasionLocale>({});

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);
      try {
        const response = await adminImageService.generateUploadUrl({ fileType: file.type });
        await adminImageService.uploadToS3(response.data.uploadUrl, file);
        setUploadedImage(response.data.imageUrl);
      } catch (error) {
        console.error('Failed to compress image:', error);
        toast.error('Failed to process image');
      }
    }
  };

  const handleSubmit = async (data: { title: string; description: string }) => {
    try {
      setLoading(true);
      if (!data.title.trim()) {
        toast.error('Experience title is required');
        setLoading(false);
        return;
      }
      if (!data.description.trim()) {
        toast.error('Experience description is required');
        setLoading(false);
        return;
      }
      if (!uploadedImage) {
        toast.error('Experience image is required');
        setLoading(false);
        return;
      }

      const finalLocale: OccasionLocale = {
        ...locale,
        [currentLang]: {
          title: data.title.trim(),
          description: data.description.trim(),
        },
      };

      // Default (top-level) title/description must always be English; other locales only in locale object
      const defaultTitle = finalLocale.en?.title ?? (currentLang === 'en' ? data.title.trim() : '');
      const defaultDescription = finalLocale.en?.description ?? (currentLang === 'en' ? data.description.trim() : '');

      await categoryService.createExperience({
        title: defaultTitle,
        description: defaultDescription,
        imageUrl: uploadedImage,
        locale: Object.keys(finalLocale).length > 0 ? finalLocale : undefined,
      });

      toast.success('Experience created successfully');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Failed to create experience:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Failed to create experience';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form<{ title: string; description: string }>
      onSubmit={handleSubmit}
      defaultValues={{ title: '', description: '' }}
      resetOnSubmit={false}
    >
      <ExperienceFormContent
        loading={loading}
        previewImage={previewImage}
        onImageUpload={handleImageUpload}
        locale={locale}
        setLocale={setLocale}
        currentLang={currentLang}
        setCurrentLang={setCurrentLang}
      />
      <Box className="add-experience-form-actions">
        <FormButton type="submit" primary disabled={loading} size="full">
          {loading ? 'Creating...' : 'Add Experience'}
        </FormButton>
        <FormButton type="button" primary={false} bordered={false} onClick={onCancel} size="full" disabled={loading}>
          Cancel
        </FormButton>
      </Box>
    </Form>
  );
};

export default AddExperienceForm;

